import React, { useCallback, useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { omit } from "lodash";

import TermPage from "./TermPage";
import {
  getDataComponentFlattenedRequestState,
  getDataComponent,
} from "../../../../../reducers/dataComponentReducer";
import {
  closeModalDialog,
  openModalDialog,
  showSnackNotificationAction,
} from "../../../../../actions/layoutActions";
import { reorderTerms } from "../../../../../actions/termsActions";
import {
  performUpdateRequest,
  setReload,
} from "../../../../../actions/dataComponentActions";
import propTypes from "../../../../../constants/propTypes";
import {
  processListRequestStatus,
  processUpdateRequestStatus,
} from "../../../../../utils/dataComponentUtils";
import { getResourcesV2 } from "../../../../../utils/bluechipUtils";
import { SCOPES_SELECT } from "../../../../../constants/DataComponents";
import withSelectedRowsAPI from "../../../../ui/BWGrid/withSelectedRowsAPI";

export const dataComponentId = "TermsGrid";

const processRequestResponse = (
  prevDataComponent,
  dataComponent,
  setReload,
  setTotalRows,
  closeModalDialog
) => {
  processUpdateRequestStatus(prevDataComponent, dataComponent, {
    onSuccess: () => {
      setReload(dataComponentId, true);
      closeModalDialog();
    },
  });
  processListRequestStatus(prevDataComponent, dataComponent, {
    onSuccess: setTotalRows,
  });
};

const getAttrs = (maxPosition, termContext, contextValue) => ({
  maxPosition,
  params: { termContext, contextValue },
});

const updateTermsMsg =
  "Please update the Entity Terms to view the recent Terms changes on the latest Project Documents";

const getOpenCreateModal = ({
  totalRows,
  termContext,
  contextValue,
  scopeId,
  openModalDialog,
  showSnackNotificationAction,
}) => filterScopeId => {
  openModalDialog(
    "Create Term",
    "CreateTerm",
    {
      ...getAttrs(totalRows.current + 1, termContext, contextValue),
      scopeId: scopeId || filterScopeId,
      onSuccess: () => {
        totalRows.current = totalRows.current + 1;
        showSnackNotificationAction(updateTermsMsg);
      },
    },
    false
  );
};

// eslint-disable-next-line max-lines-per-function
const TermContainer = ({
  dataComponent,
  flattenedDataComponent,
  openModalDialog,
  termContext,
  contextValue,
  scopeId,
  setReload,
  reorderTerms,
  additionalFilters,
  disableSyncGlobalTerms,
  disableAddTerm,
  showSnackNotificationAction,
  scopes,
  performUpdateRequest,
  closeModalDialog,
  selectedRows,
}) => {
  const [prevDataComponent, setPrevDataComponent] = useState(dataComponent);
  const totalRows = useRef(flattenedDataComponent.totalRows);

  useEffect(() => {
    setPrevDataComponent(dataComponent);
  }, [dataComponent]);
  useEffect(() => {
    processRequestResponse(
      prevDataComponent,
      dataComponent,
      setReload,
      () => (totalRows.current = flattenedDataComponent.totalRows),
      closeModalDialog
    );
  });

  const handleOpenCreateModal = useCallback(
    getOpenCreateModal({
      totalRows,
      contextValue,
      openModalDialog,
      scopeId,
      showSnackNotificationAction,
      termContext,
    }),
    [
      openModalDialog,
      termContext,
      contextValue,
      scopeId,
      showSnackNotificationAction,
    ]
  );

  const handleOpenEditModal = useCallback(
    term => {
      openModalDialog(
        `Edit Term`,
        "EditTerm",
        {
          term,
          ...getAttrs(totalRows.current, termContext, contextValue, totalRows),
          onSuccess: () => {
            showSnackNotificationAction(updateTermsMsg);
          },
        },
        false
      );
    },
    [openModalDialog, termContext, contextValue, showSnackNotificationAction]
  );

  const deactivateTerms = useCallback(
    terms => () => {
      performUpdateRequest(
        dataComponentId,
        terms.map(term => ({
          ...omit(term, ["__expanded__", "__isClicked__", "__json_api__"]),
          isActive: false,
        })),
        { termContext, contextValue }
      );
    },
    [contextValue, performUpdateRequest, termContext]
  );

  const handleOpenDeleteModal = useCallback(
    term => {
      const { id, content } = term;
      const truncatedContent =
        content.length > 30 ? `${content.substr(0, 30)}...` : content;
      const title = `"${truncatedContent}"`;
      openModalDialog(
        `Delete ${title}`,
        "StandardDelete",
        {
          id,
          title,
          dataComponentId,
          params: { termContext, contextValue },
          onDelete: deactivateTerms([term]),
          onSuccess: () => {
            totalRows.current = totalRows.current - 1;
            showSnackNotificationAction(updateTermsMsg);
          },
        },
        false
      );
    },
    [
      openModalDialog,
      termContext,
      contextValue,
      deactivateTerms,
      showSnackNotificationAction,
    ]
  );

  const handleOpenBulkDeleteModal = useCallback(() => {
    openModalDialog(
      "Delete Terms",
      "StandardBulkDeleteConfirmation",
      {
        dataComponentId,
        selectedIds: flattenedDataComponent.selectedIds,
        params: { termContext, contextValue },
        onDelete: deactivateTerms(selectedRows),
        onSuccess: () => {
          totalRows.current =
            totalRows.current - flattenedDataComponent.selectedIds.length;
          showSnackNotificationAction(updateTermsMsg);
        },
      },
      false
    );
  }, [
    openModalDialog,
    flattenedDataComponent.selectedIds,
    termContext,
    contextValue,
    deactivateTerms,
    selectedRows,
    showSnackNotificationAction,
  ]);

  const handleOpenBulkUpload = useCallback(() => {
    openModalDialog(
      "Upload Terms",
      "TermsBulkUpload",
      {
        dataComponentId,
        additionalValues: { clientId: contextValue, scopeId },
        pluralEntity: "TERMS",
      },
      false
    );
  }, [openModalDialog, contextValue, scopeId]);

  const handleUpdateEntityTerms = useCallback(() => {
    openModalDialog(
      `Update All Entity Terms`,
      "BulkUpdateTerms",
      { isGlobal: true },
      false,
      false
    );
  }, [openModalDialog]);

  const handleReorder = useCallback(
    ({ newIndex }, item) => {
      showSnackNotificationAction(updateTermsMsg);
      reorderTerms(dataComponentId, item.id, newIndex, {
        termContext,
        contextValue,
      });
    },
    [showSnackNotificationAction, reorderTerms, termContext, contextValue]
  );

  return (
    <TermPage
      dataComponent={flattenedDataComponent}
      additionalFilters={additionalFilters}
      onOpenCreateModal={handleOpenCreateModal}
      onOpenEditModal={handleOpenEditModal}
      onOpenDeleteModal={handleOpenDeleteModal}
      onOpenBulkDeleteModal={handleOpenBulkDeleteModal}
      onOpenBulkUpload={handleOpenBulkUpload}
      onUpdateEntityTerms={handleUpdateEntityTerms}
      onReorder={handleReorder}
      termContext={termContext}
      contextValue={contextValue}
      disableSyncGlobalTerms={disableSyncGlobalTerms}
      disableAddTerm={disableAddTerm}
      scopes={scopes}
      selectedRows={selectedRows}
    />
  );
};

TermContainer.propTypes = {
  additionalFilters: PropTypes.shape({}),
  dataComponent: propTypes.dataComponent,
  flattenedDataComponent: propTypes.dataComponent,
  openModalDialog: PropTypes.func.isRequired,
  showSnackNotificationAction: PropTypes.func.isRequired,
  performUpdateRequest: PropTypes.func.isRequired,
  closeModalDialog: PropTypes.func.isRequired,
  reorderTerms: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  termContext: PropTypes.string,
  contextValue: PropTypes.string,
  disableSyncGlobalTerms: PropTypes.bool,
  disableAddTerm: PropTypes.bool,
  scopeId: PropTypes.string,
  scopes: PropTypes.array,
  selectedRows: PropTypes.array,
};

export const mapStateToProps = state => {
  const scopesDataComponent = getDataComponentFlattenedRequestState(
    SCOPES_SELECT,
    state
  );
  return {
    flattenedDataComponent: getDataComponentFlattenedRequestState(
      dataComponentId,
      state
    ),
    dataComponent: getDataComponent(dataComponentId, state),
    scopes: getResourcesV2(scopesDataComponent, state) || [],
  };
};

export const mapDispatchToProps = {
  openModalDialog,
  setReload,
  reorderTerms,
  showSnackNotificationAction,
  performUpdateRequest,
  closeModalDialog,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withSelectedRowsAPI(TermContainer));
